import {
  ComponentPropsWithoutRef,
  forwardRef,
  useId,
  useMemo,
  useState,
} from "react";
import { LayoutUtilProps } from "../../types";
import { DetailsSummary } from "./DetailsSummary";
import { DetailsContent } from "./DetailsContent";
import { DetailsContext } from "./internal/DetailsContext";
import { useLayoutPropsUtil } from "../../internal/hooks";

import cx from "classnames";
import styles from "./Details.module.scss";

/**
 * Props for the Details component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type DetailsProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    /**
     * Determines the controlled expanded state of the details.
     * @remarks Use this to make the details expanded state controlled.
     * @default false
     */
    expanded?: boolean;
    /**
     * Determines the default expanded state of the details.
     * @remarks Use this to make the details expanded state uncontrolled.
     * @default false
     */
    defaultExpanded?: boolean;
  };

/**
 * Details component for creating expandable/collapsible content sections.
 *
 * Features:
 * - Expandable and collapsible content sections
 * - Controlled and uncontrolled state management
 * - Accessible with proper ARIA attributes
 * - Smooth animations for expand/collapse
 * - Keyboard navigation support
 * - Layout utility props for positioning and spacing
 * - Compound component pattern with Summary, Content, and Arrow sub-components
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Details defaultExpanded>
 *   <Details.Summary>Click to expand</Details.Summary>
 *   <Details.Content>This content is now visible</Details.Content>
 * </Details>
 */
export const Details = Object.assign(
  forwardRef<HTMLDivElement, DetailsProps>(function DetailsInner(props, ref) {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      children,
      id: idProp,
      expanded: expandedControlled,
      defaultExpanded = false,
      style,
      className,
      ...rest
    } = componentProps;
    const id = useId();
    const [isExpanded, setIsOpen] = useState(defaultExpanded);

    function expanded() {
      setIsOpen(true);
    }

    function close() {
      setIsOpen(false);
    }

    function toggle() {
      setIsOpen((prevIsOpen: boolean) => !prevIsOpen);
    }

    const value = useMemo(
      () => ({
        id: idProp != null ? idProp : id,
        isExpanded:
          expandedControlled != null ? expandedControlled : isExpanded,
        expanded,
        close,
        toggle,
      }),
      [id, idProp, isExpanded, expandedControlled],
    );

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <DetailsContext.Provider value={value}>
        <div
          data-anv="details"
          ref={ref}
          className={cx(styles.details, className)}
          style={styleCombined}
          {...rest}
        >
          {children}
        </div>
      </DetailsContext.Provider>
    );
  }),
  {
    Summary: DetailsSummary,
    Content: DetailsContent,
  },
);
Details.displayName = "Details";
